const APP_CACHE_DB_NAME = 'ggc_app_cache';
const APP_CACHE_STORE_NAME = 'entries';
const APP_CACHE_DB_VERSION = 1;

/** 공통 IndexedDB 캐시 TTL (5분) */
export const INDEXED_CACHE_TTL_MS = 5 * 60 * 1000;

export interface IndexedDbCacheEntry<T> {
  key: string;
  value: T;
  updatedAt: number;
  expiresAt: number;
}

function isIndexedDbSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

let _cachedDb: IDBDatabase | null = null;

const OPEN_DB_TIMEOUT_MS = 3000;

function openAppCacheDb(): Promise<IDBDatabase> {
  if (_cachedDb) {
    try {
      // DB 연결이 유효한지 확인 (트랜잭션 생성 시도)
      _cachedDb.transaction(APP_CACHE_STORE_NAME, 'readonly');
      return Promise.resolve(_cachedDb);
    } catch {
      _cachedDb = null;
    }
  }

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('IndexedDB open timed out'));
    }, OPEN_DB_TIMEOUT_MS);

    const request = indexedDB.open(APP_CACHE_DB_NAME, APP_CACHE_DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(APP_CACHE_STORE_NAME)) {
        db.createObjectStore(APP_CACHE_STORE_NAME, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => {
      clearTimeout(timer);
      const db = request.result;
      db.onclose = () => { _cachedDb = null; };
      db.onversionchange = () => { db.close(); _cachedDb = null; };
      _cachedDb = db;
      resolve(db);
    };
    request.onerror = () => {
      clearTimeout(timer);
      reject(request.error);
    };
    request.onblocked = () => {
      clearTimeout(timer);
      reject(new Error('IndexedDB open blocked'));
    };
  });
}

export async function getIndexedDbCacheEntry<T>(key: string): Promise<IndexedDbCacheEntry<T> | null> {
  if (!key || !isIndexedDbSupported()) {
    return null;
  }

  try {
    const db = await openAppCacheDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(APP_CACHE_STORE_NAME, 'readonly');
      const request = tx.objectStore(APP_CACHE_STORE_NAME).get(key);

      request.onsuccess = () => {
        resolve((request.result as IndexedDbCacheEntry<T> | undefined) ?? null);
      };
      request.onerror = () => {
        resolve(null);
      };
    });
  } catch {
    return null;
  }
}

export async function setIndexedDbCacheEntry<T>(key: string, value: T, ttlMs: number): Promise<void> {
  if (!key || !isIndexedDbSupported() || ttlMs <= 0) {
    return;
  }

  try {
    const db = await openAppCacheDb();
    const now = Date.now();
    const tx = db.transaction(APP_CACHE_STORE_NAME, 'readwrite');
    tx.objectStore(APP_CACHE_STORE_NAME).put({
      key,
      value,
      updatedAt: now,
      expiresAt: now + ttlMs,
    } satisfies IndexedDbCacheEntry<T>);
    await new Promise<void>((resolve) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    // IndexedDB 실패 시 캐시 없이 계속 진행
  }
}

export async function clearIndexedDbCacheByPrefix(prefix: string): Promise<void> {
  if (!prefix || !isIndexedDbSupported()) {
    return;
  }

  try {
    const db = await openAppCacheDb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(APP_CACHE_STORE_NAME, 'readwrite');
      const store = tx.objectStore(APP_CACHE_STORE_NAME);
      const request = store.openCursor();
      let resolved = false;

      const finish = () => {
        if (resolved) {
          return;
        }

        resolved = true;
        resolve();
      };

      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          return;
        }

        if (typeof cursor.key === 'string' && cursor.key.startsWith(prefix)) {
          cursor.delete();
        }
        cursor.continue();
      };

      request.onerror = finish;
      tx.oncomplete = finish;
      tx.onerror = finish;
      tx.onabort = finish;
    });

  } catch {
    // 캐시 삭제 실패는 치명적이지 않음
  }
}

/* ══════════════════════════════════════════════════════════
   Heavy API 캐시 레지스트리
   — 각 API 모듈이 registerHeavyCache()로 등록하면
     clearAllHeavyCaches / getHeavyCacheMinRemainingMs 가 자동으로 동작
   ══════════════════════════════════════════════════════════ */

interface HeavyCacheEntry {
  prefix: string;
  clear: () => Promise<void>;
}

const _heavyCacheRegistry: HeavyCacheEntry[] = [];

/** 무거운 API 캐시를 레지스트리에 등록. 앱 초기화 시 각 API 모듈에서 호출. */
export function registerHeavyCache(prefix: string, clearFn: () => Promise<void>): void {
  if (!_heavyCacheRegistry.some((e) => e.prefix === prefix)) {
    _heavyCacheRegistry.push({ prefix, clear: clearFn });
  }
}

/** 등록된 모든 무거운 API 캐시를 삭제 */
export async function clearAllHeavyCaches(): Promise<void> {
  await Promise.all(_heavyCacheRegistry.map((e) => e.clear()));
}

/** 등록된 모든 무거운 API 캐시 중 가장 빨리 만료되는 항목까지 남은 시간(ms). 없으면 null. */
export async function getHeavyCacheMinRemainingMs(): Promise<number | null> {
  const prefixes = _heavyCacheRegistry.map((e) => e.prefix);
  if (prefixes.length === 0) return null;
  return getMinRemainingMsForKeyPrefixes(prefixes);
}

/* ──────────────────── Heavy refresh in-flight tracker ────────────────────
 * Tracks active cache-miss network refreshes for heavy caches so UI (e.g.
 * the global PageLoader) can show a "캐시 데이터를 갱신중입니다" message. */
let _activeHeavyRefreshes = 0;
const _heavyRefreshSubscribers: Set<() => void> = new Set();

function notifyHeavyRefresh(): void {
  for (const cb of _heavyRefreshSubscribers) cb();
}

/** Manual hook — caller wraps its own fetch so the UI tracker stays in sync.
 *  Used by APIs that don't go through `createCachedApi`. */
export function beginHeavyRefresh(): void {
  _activeHeavyRefreshes += 1;
  notifyHeavyRefresh();
}

export function endHeavyRefresh(): void {
  _activeHeavyRefreshes = Math.max(0, _activeHeavyRefreshes - 1);
  notifyHeavyRefresh();
}

export function getActiveHeavyRefreshCount(): number {
  return _activeHeavyRefreshes;
}

export function subscribeHeavyRefresh(cb: () => void): () => void {
  _heavyRefreshSubscribers.add(cb);
  return () => _heavyRefreshSubscribers.delete(cb);
}

/* ══════════════════════════════════════════════════════════
   createCachedApi — 캐시 패턴 공통화 헬퍼
   사용 예:
     const { fetch, clear, CACHE_KEY_PREFIX } = createCachedApi<MyData[]>({
       prefix: 'myApi.getData::',
       fetcher: () => webApi3.post('myApi.getData', {}),
       normalize: (data) => Array.isArray(data) ? data : [],
     });
   ══════════════════════════════════════════════════════════ */

export interface CachedApiOptions<T> {
  /** IndexedDB 캐시 키 접두사 (예: 'campaigns.getCampaigns::') */
  prefix: string;
  /** 실제 API 호출 함수 */
  fetcher: (params: Record<string, unknown>) => Promise<T>;
  /** 응답 정규화 (기본: identity) */
  normalize?: (data: T) => T;
  /** 캐시 저장 조건 (기본: truthy) */
  shouldCache?: (data: T) => boolean;
  /** heavy 캐시 레지스트리에 등록 여부 (기본: true) */
  heavy?: boolean;
}

export interface CachedApi<T> {
  /** 캐시 키 접두사 */
  CACHE_KEY_PREFIX: string;
  /** 캐시 우선 조회, 미스 시 API 호출 후 캐시 저장 */
  fetch: (params?: Record<string, unknown>, options?: { forceRefresh?: boolean }) => Promise<T>;
  /** 이 API의 캐시만 삭제 */
  clear: () => Promise<void>;
}

export function createCachedApi<T>(opts: CachedApiOptions<T>): CachedApi<T> {
  const { prefix, fetcher, normalize = (d) => d, shouldCache = Boolean, heavy = true } = opts;
  const pending = new Map<string, Promise<T>>();

  function cacheKey(params: Record<string, unknown>): string {
    return `${prefix}${JSON.stringify(stableSort(params))}`;
  }

  async function clear(): Promise<void> {
    pending.clear();
    await clearIndexedDbCacheByPrefix(prefix);
  }

  async function fetchFn(
    params: Record<string, unknown> = {},
    options: { forceRefresh?: boolean } = {},
  ): Promise<T> {
    const key = cacheKey(params);

    const cached = options.forceRefresh ? null : await getIndexedDbCacheEntry<T>(key);
    if (cached && cached.expiresAt > Date.now()) {
      return normalize(cached.value);
    }

    const inflight = pending.get(key);
    if (inflight) return inflight;

    const request = (async () => {
      if (heavy) beginHeavyRefresh();
      try {
        const raw = await fetcher(params);
        const data = normalize(raw);
        if (shouldCache(data)) {
          await setIndexedDbCacheEntry(key, data, INDEXED_CACHE_TTL_MS);
        }
        return data;
      } catch (error) {
        if (cached?.value) return normalize(cached.value);
        throw error;
      } finally {
        pending.delete(key);
        if (heavy) endHeavyRefresh();
      }
    })();

    pending.set(key, request);
    return request;
  }

  if (heavy) {
    registerHeavyCache(prefix, clear);
  }

  return { CACHE_KEY_PREFIX: prefix, fetch: fetchFn, clear };
}

/** 객체 키를 정렬하여 캐시 키 안정성 보장 */
function stableSort(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableSort);
  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        const v = (value as Record<string, unknown>)[key];
        if (v !== undefined) acc[key] = stableSort(v);
        return acc;
      }, {});
  }
  return value;
}

/**
 * 지정한 키 접두사에 해당하는 항목 중, 아직 만료 전인 것만 보고
 * 가장 빨리 만료되는 시점까지 남은 시간(ms)을 반환한다. 해당 항목이 없으면 null.
 */
export async function getMinRemainingMsForKeyPrefixes(
  prefixes: readonly string[],
): Promise<number | null> {
  if (!prefixes.length || !isIndexedDbSupported()) {
    return null;
  }

  const now = Date.now();

  try {
    const db = await openAppCacheDb();
    return await new Promise<number | null>((resolve) => {
      const tx = db.transaction(APP_CACHE_STORE_NAME, 'readonly');
      const store = tx.objectStore(APP_CACHE_STORE_NAME);
      const request = store.openCursor();
      let best: number | null = null;

      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          return;
        }

        const key = cursor.key;
        const entry = cursor.value as IndexedDbCacheEntry<unknown>;
        if (
          typeof key === 'string' &&
          prefixes.some((p) => key.startsWith(p)) &&
          typeof entry?.expiresAt === 'number' &&
          entry.expiresAt > now
        ) {
          const rem = entry.expiresAt - now;
          if (best === null || rem < best) {
            best = rem;
          }
        }
        cursor.continue();
      };

      request.onerror = () => resolve(null);

      tx.oncomplete = () => resolve(best);
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    });
  } catch {
    return null;
  }
}
